import { CheckCircle2, Clock, Mail, RefreshCw, LogOut, ShieldCheck, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AuthLayout } from '@/components/auth/AuthLayout';

interface PendingInvitationInfo {
  email: string;
  workspaceName: string;
  expiresAt?: string | null;
}

interface NoWorkspaceAccessProps {
  /** Email of the connected user, displayed for reassurance. */
  userEmail?: string | null;
  /** If the user has at least one pending invitation in DB, pass it for context. */
  pendingInvitation?: PendingInvitationInfo | null;
  /** Called when the user clicks "Refresh access". */
  onRefresh?: () => void;
  /** Called when the user clicks "Sign out". */
  onSignOut?: () => void;
  /** Display as a compact banner instead of a full-page state. */
  compact?: boolean;
  /** Whether a refresh is currently in progress (disables the button). */
  refreshing?: boolean;
}

/**
 * Affiche un écran (ou un bandeau compact) lorsque l'utilisateur est
 * authentifié mais n'a accès à aucun espace de travail. Détaille la
 * procédure de validation pour activer l'accès.
 */
export function NoWorkspaceAccess({
  userEmail,
  pendingInvitation,
  onRefresh,
  onSignOut,
  compact = false,
  refreshing = false,
}: NoWorkspaceAccessProps) {
  const hasInvitation = !!pendingInvitation;

  // Compact banner variant (for in-app placement)
  if (compact) {
    return (
      <Alert className="border-warning/40 bg-warning/5">
        <Clock className="h-4 w-4 text-warning" />
        <AlertTitle className="text-foreground">
          Accès en attente de validation
        </AlertTitle>
        <AlertDescription className="space-y-2 text-sm">
          <p>
            {hasInvitation ? (
              <>
                Votre invitation à rejoindre{' '}
                <span className="font-medium text-foreground">
                  {pendingInvitation!.workspaceName}
                </span>{' '}
                est enregistrée. L'accès s'activera dès la confirmation de votre adresse email.
              </>
            ) : (
              <>Vous n'avez pas encore accès à un espace de travail.</>
            )}
          </p>
          {onRefresh && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={refreshing}
              aria-label="Actualiser l'accès au workspace"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
              Actualiser
            </Button>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  // Full-page state
  return (
    <AuthLayout>
      <div className="space-y-8">
        <div className="mx-auto w-24 h-24 rounded-full bg-warning/10 flex items-center justify-center">
          <Clock className="w-12 h-12 text-warning" aria-hidden="true" />
        </div>

        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-warning/10 text-warning text-xs font-medium">
            <Clock className="w-3.5 h-3.5" aria-hidden="true" />
            En attente de validation
          </div>
          <h1 className="text-3xl font-bold text-foreground">
            Accès au workspace en attente
          </h1>
          <p className="text-muted-foreground">
            {hasInvitation ? (
              <>
                Votre invitation à rejoindre{' '}
                <span className="text-foreground font-medium">
                  {pendingInvitation!.workspaceName}
                </span>{' '}
                est enregistrée, mais l'accès n'est pas encore activé.
              </>
            ) : (
              <>
                Vous êtes connecté(e), mais aucun espace de travail ne vous est encore accessible.
              </>
            )}
          </p>
          {userEmail && (
            <p className="text-sm text-muted-foreground">
              Compte connecté : <span className="text-foreground font-medium">{userEmail}</span>
            </p>
          )}
        </div>

        {/* Procedure */}
        <div className="space-y-3">
          <p className="text-sm font-medium text-foreground">Procédure de validation :</p>
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success/15 text-success text-xs font-semibold">
                1
              </span>
              <div className="space-y-0.5 pt-0.5">
                <p className="text-sm text-foreground font-medium inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-success" aria-hidden="true" />
                  Compte créé
                </p>
                <p className="text-sm text-muted-foreground">
                  Votre identifiant{userEmail ? <> <span className="font-medium text-foreground">{userEmail}</span></> : ''} est bien enregistré.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary text-xs font-semibold">
                2
              </span>
              <div className="space-y-0.5 pt-0.5">
                <p className="text-sm text-foreground font-medium inline-flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                  Confirmation de l'adresse email
                </p>
                <p className="text-sm text-muted-foreground">
                  Cliquez sur le lien envoyé par email (pensez aux dossiers <strong>spam</strong> et <strong>courrier indésirable</strong>) pour valider l'adresse.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary text-xs font-semibold">
                3
              </span>
              <div className="space-y-0.5 pt-0.5">
                <p className="text-sm text-foreground font-medium inline-flex items-center gap-1.5">
                  <UserCheck className="w-3.5 h-3.5 text-primary" aria-hidden="true" />
                  Activation par l'administrateur
                </p>
                <p className="text-sm text-muted-foreground">
                  {hasInvitation ? (
                    <>
                      L'administrateur de{' '}
                      <span className="font-medium text-foreground">
                        {pendingInvitation!.workspaceName}
                      </span>{' '}
                      a déjà créé votre invitation. Aucune action supplémentaire de sa part n'est requise.
                    </>
                  ) : (
                    <>
                      Demandez à l'administrateur de l'espace de vous envoyer une invitation à votre adresse email.
                    </>
                  )}
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                4
              </span>
              <div className="space-y-0.5 pt-0.5">
                <p className="text-sm text-foreground font-medium">
                  Accès activé automatiquement
                </p>
                <p className="text-sm text-muted-foreground">
                  Cliquez sur <span className="font-medium text-foreground">Actualiser l'accès</span> ci-dessous une fois ces étapes terminées : la page se rechargera et vous accèderez à l'espace.
                </p>
              </div>
            </li>
          </ol>
        </div>

        {/* Reassurance */}
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4">
          <ShieldCheck className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" aria-hidden="true" />
          <p className="text-xs text-muted-foreground">
            Cette validation garantit que seules les personnes autorisées par l'administrateur de l'espace peuvent accéder aux tickets et données clients.
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {onRefresh && (
            <Button
              type="button"
              className="w-full"
              onClick={onRefresh}
              disabled={refreshing}
              aria-label="Actualiser l'accès au workspace"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
              {refreshing ? 'Vérification...' : "Actualiser l'accès"}
            </Button>
          )}
          {onSignOut && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={onSignOut}
              aria-label="Se déconnecter"
            >
              <LogOut className="w-4 h-4 mr-2" aria-hidden="true" />
              Se déconnecter
            </Button>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}

export default NoWorkspaceAccess;
